import { connect } from 'react-redux';
import { useEffect, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import EmojiPicker from 'emoji-picker-react';

import { hasCompletedOnboarding, getOnboardingRoute } from '../../utils/AuthorizationCheck';

const WeeklyContextInput = ({auth, showDropdown = false}) => {        
    const history = useHistory();
    const [weeklyContext, setWeeklyContext] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentWeek, setCurrentWeek] = useState(null);
    const [isLoadingWeek, setIsLoadingWeek] = useState(true);
    const [isInitializing, setIsInitializing] = useState(true);
    const [showExamples, setShowExamples] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [selectedWeek, setSelectedWeek] = useState(null);
    const [upcomingWeek, setUpcomingWeek] = useState(null);
    const [platform_league_id, setPlatform_league_id] = useState(null);

    const maxCharacters = 10000;

    const loadExistingWeeklyContext = useCallback(async () => {
        try {
            // Skip loading context in dropdown mode - fetchUpcomingWeek will handle it
            if (showDropdown) {
                return;
            }
            
            // Get platform_league_id from database
            if (auth?.user?.id) {
                const response = await axios.get('/api/get/user-league-data');
                if (response.data.success) {
                    const platform_league_id = response.data.platform_league_id;
                    
                    // Fetch weekly context from database
                    const contextResponse = await axios.get(`/api/get/weekly-context/${platform_league_id}`);
                    if (contextResponse.data.success && contextResponse.data.week_context) {
                        setWeeklyContext(contextResponse.data.week_context);
                        return;
                    }
                }
            }
            
            // If no data found, textarea stays empty with placeholder
            
        } catch (error) {
            console.error('Error loading existing weekly context:', error);
        }
    }, [auth?.user?.id, showDropdown]);

    useEffect(() => {                
        // Only run onboarding checks if not in dropdown mode
        if (!showDropdown) {
        // Check if user is authenticated and has completed onboarding
        if (auth?.user?.id && hasCompletedOnboarding(auth)) {
            history.push('/dashboard');
            return;
        }
        
        // Check if user should be on a different onboarding step
        if (auth?.user?.id && !hasCompletedOnboarding(auth)) {
            const correctRoute = getOnboardingRoute(auth);
            if (correctRoute !== '/onboarding/weekly-context') {
                history.push(correctRoute);
                return;
            }
        }
        }
        
        // Load platform_league_id for both modes
        const loadPlatformLeagueId = async () => {
            try {
                if (auth?.user?.id) {
                    const response = await axios.get('/api/get/user-league-data');
                    if (response.data.success) {
                        setPlatform_league_id(response.data.platform_league_id);
                    }
                }
            } catch (error) {
                console.error('Error loading platform league id:', error);
            }
        };
        
        loadPlatformLeagueId();
        
        // Load existing weekly context data (database first, localStorage fallback)
        loadExistingWeeklyContext();

        // Fetch current week information
        fetchCurrentWeek();
        
        // Mark initialization as complete after all checks
        setIsInitializing(false);
    }, [auth, history, loadExistingWeeklyContext, showDropdown]);

    // New functions for dropdown functionality
    const fetchUpcomingWeek = useCallback(async () => {
        try {
            if (auth?.user?.id && platform_league_id) {
                const response = await axios.get(`/api/get/upcoming-week/${platform_league_id}`);
                if (response.data.success) {
                    setUpcomingWeek(response.data.upcoming_week);
                    // Set default selected week to upcoming week
                    setSelectedWeek(response.data.upcoming_week);
                    
                    // Load context for the upcoming week
                    const contextResponse = await axios.get(`/api/get/weekly-context/${platform_league_id}/${response.data.season}/${response.data.upcoming_week}`);
                    if (contextResponse.data.success) {
                        setWeeklyContext(contextResponse.data.week_context || '');
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching upcoming week:', error);
        }
    }, [auth?.user?.id, platform_league_id]);

    // Fetch upcoming week when in dropdown mode
    useEffect(() => {
        if (showDropdown && platform_league_id) {
            fetchUpcomingWeek();
        }
    }, [showDropdown, platform_league_id, fetchUpcomingWeek]);

    // Close emoji picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showEmojiPicker && !event.target.closest('.emoji-picker-container')) {
                setShowEmojiPicker(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showEmojiPicker]);


    const fetchCurrentWeek = async () => {
        try {
            setIsLoadingWeek(true);
            
            const response = await axios.get('/api/get/current-week');
            
            if (response.data.success && response.data.week) {
                setCurrentWeek({
                    season: response.data.season,
                    week: response.data.week,
                    sport: response.data.sport
                });
            } else {
                console.error('Current week API returned success but no week data:', response.data);
                // Send Slack alert for missing week data
                try {
                    await axios.post('/api/slack/alert', {
                        message: `🚨 CRITICAL: Current week API returned no week data. Response: ${JSON.stringify(response.data)}`,
                        channel: '#alerts'
                    });
                } catch (slackError) {
                    console.error('Failed to send Slack alert:', slackError);
                }
                setCurrentWeek(null);
            }
        } catch (error) {
            console.error('Error fetching current week:', error);
            // Send Slack alert for API failure
            try {
                await axios.post('/api/slack/alert', {
                    message: `🚨 CRITICAL: Current week API failed. Error: ${error.message}`,
                    channel: '#alerts'
                });
            } catch (slackError) {
                console.error('Failed to send Slack alert:', slackError);
            }
            setCurrentWeek(null);
        } finally {
            setIsLoadingWeek(false);
        }
    };

    const getExampleText = () => {
        if (isLoadingWeek) {
            return "Loading examples...";
        }

        return `**This week** the league chat has been absolutely wild.

• **Team Alpha** has been talking so much trash about their "guaranteed win" 🙄
• **Team Beta** and **Team Gamma** made a trade that everyone thinks was pretty stupid
• **Team Delta** is already complaining about their matchup being "unfair"
• The commissioner had to step in because **Team Echo** was being way too toxic in the group chat 😤`;
    };

    const getCurrentWeekDisplay = () => {
        if (isLoadingWeek) return 'Loading...';
        if (!currentWeek) return 'Current Week';
        
        return `${currentWeek.season} - Week ${currentWeek.week}`;
    };

    const getWeekOptions = () => {
        if (!currentWeek) return [];
        
        const options = [];
        const nextWeek = currentWeek.week + 1;
        
        // Add "next" week first
        if (nextWeek <= 18) {
            options.push({
                week: nextWeek,
                label: `Week ${nextWeek} (Next)`,
                editable: true
            });
        }
        
        // Add prior weeks (current week and before)
        for (let week = currentWeek.week; week >= 1; week--) {
            options.push({
                week: week,
                label: `Week ${week}`,
                editable: false
            });
        }
        
        return options;
    };

    const handleWeekSelection = async (selectedWeekValue) => {
        if (!selectedWeekValue || !platform_league_id) return;
        
        setSelectedWeek(selectedWeekValue);
        setIsLoadingWeek(true);
        
        try {
            // Fetch context for selected week
            const response = await axios.get(`/api/get/weekly-context/${platform_league_id}/${currentWeek.season}/${selectedWeekValue}`);
            
            if (response.data.success) {
                setWeeklyContext(response.data.week_context || '');
            }
        } catch (error) {
            console.error('Error fetching weekly context:', error);
        } finally {
            setIsLoadingWeek(false);
        }
    };

    const isSaveEnabled = () => {
        // Only enable save for "next" week (current + 1)
        return selectedWeek === upcomingWeek && weeklyContext.trim().length > 0;
    };

    const handleTextChange = (e) => {
        const newText = e.target.value;
        if (newText.length <= maxCharacters) {
            setWeeklyContext(newText);
        }
    };

    const formatText = (type) => {
        const textarea = document.getElementById('weeklyContextTextarea');
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = weeklyContext.substring(start, end);
        
        if (selectedText) {
            let formattedText = '';
            if (type === 'bold') {
                formattedText = `**${selectedText}**`;
            } else if (type === 'bullet') {
                formattedText = `• ${selectedText}`;
            }
            
            const newText = weeklyContext.substring(0, start) + formattedText + weeklyContext.substring(end);
            if (newText.length <= maxCharacters) {
                setWeeklyContext(newText);
                
                // Restore cursor position
                setTimeout(() => {
                    textarea.focus();
                    textarea.setSelectionRange(start + formattedText.length, start + formattedText.length);
                }, 0);
            }
        }
    };

    const onEmojiClick = (emojiData) => {
        const textarea = document.getElementById('weeklyContextTextarea');
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        
        const newText = weeklyContext.substring(0, start) + emojiData.emoji + weeklyContext.substring(end);
        if (newText.length <= maxCharacters) {
            setWeeklyContext(newText);
            
            // Restore cursor position
            setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(start + emojiData.emoji.length, start + emojiData.emoji.length);
            }, 0);
        }
        
        // Close emoji picker after selection
        setShowEmojiPicker(false);
    };

    const handleSkip = async () => {
        setIsSubmitting(true);
        
        try {
            // Save empty weekly context to backend if authenticated
            if (auth?.user?.id) {
                // Get league data from database
                const response = await axios.get('/api/get/user-league-data');
                if (response.data.success) {
                    const platform_league_id = response.data.platform_league_id;
                    
                    await axios.post('/api/post/weekly-context', {
                        week_context: '',
                        platform_league_id: platform_league_id,
                        season: currentWeek?.season,
                        week: currentWeek?.week
                    });
                    
                    // Update onboarding step to 8 after successful weekly context save
                    await axios.post('/api/post/onboarding-step/8');
                }
            }
            
            // Move to next step
            history.push('/onboarding/plan');
        } catch (error) {
            console.error('Error skipping weekly context:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleContinue = async () => {
        setIsSubmitting(true);
        
        try {
            // Save weekly context to backend if authenticated
            if (auth?.user?.id) {
                // Get league data from database
                const response = await axios.get('/api/get/user-league-data');
                if (!response.data.success) {
                    throw new Error('League information not found. Please restart onboarding from league selection.');
                }
                
                const platform_league_id = response.data.platform_league_id;
                
                // Use selected week if in dropdown mode, otherwise use current week
                const targetWeek = showDropdown ? selectedWeek : currentWeek?.week;
                const targetSeason = currentWeek?.season;
                
                await axios.post('/api/post/weekly-context', {
                    week_context: weeklyContext.trim(),
                    platform_league_id: platform_league_id,
                    season: targetSeason,
                    week: targetWeek
                });
                
                // Update onboarding step to 8 after successful weekly context save (only for onboarding mode)
                if (!showDropdown) {
                await axios.post('/api/post/onboarding-step/8');
                }
            }
            
            // Move to next step (only for onboarding mode) or redirect to dashboard (for dropdown mode)
            if (!showDropdown) {
            history.push('/onboarding/plan');
            } else {
                // Redirect to dashboard after successful save in dropdown mode
                history.push('/dashboard');
            }
        } catch (error) {
            console.error('Error saving weekly context:', error);
            // Show user-friendly error message
            alert('Failed to save weekly context. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getProgressPercentage = () => {
        // Weekly context is step 7 of 8, so (7-1)/8 = 75%
        return 75;
    };

    const getRemainingCharacters = () => {
        return maxCharacters - weeklyContext.length;
    };

    // Show loading state while initializing
    if (isInitializing) {
        return (
            <div className="pt-5 onboarding-section">    
                <Row className="onboarding-header">
                    <Col xs={1} md={2}></Col>
                    <Col xs={10} md={8}>       
                            
                        <Row className="text-center onboarding-title-row">
                            <Col className="onboarding-title">Loading Weekly Context</Col>
                        </Row>
                        
                        {/* Progress Bar */}
                        <Row className="mt-4">
                            <Col>
                                <div className="text-center mb-1">
                                    <span className="onboarding-progress-percentage">
                                        {Math.round(getProgressPercentage())}% Complete
                                    </span>
                                </div>
                                <div className="onboarding-progress">
                                    <div 
                                        className="onboarding-progress-bar"
                                        style={{ width: `${getProgressPercentage()}%` }}
                                    ></div>
                                </div>
                            </Col>
                        </Row>

                        <Row className="pt-4">
                            <Col>
                                <div className="text-center">
                                    <Spinner animation="border" role="status" className="mb-3">
                                        <span className="visually-hidden">Loading...</span>
                                    </Spinner>
                                    <h4 className="text-white">Processing...</h4>
                                </div>
                            </Col>
                        </Row>                        
                    </Col>                            
                </Row>
            </div>
        );
    }

    return (
        <>
            {showDropdown ? (
                // Dashboard mode - render content directly without Row/Col wrapper
                <>
                    {/* Examples Section */}

                        {/* Examples Section */}
                        {showExamples && (
                            <Row className="mt-4">
                                <Col>
                                    <div className="weekly-context-examples">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <h6 className="examples-title mb-0">💡 Example</h6>
                                            <Button 
                                                variant="link" 
                                                size="sm" 
                                                className="text-muted p-0"
                                                onClick={() => setShowExamples(false)}
                                            >
                                                Hide
                                            </Button>
                                        </div>
                                        <div className="examples-content">
                                            <div dangerouslySetInnerHTML={{ 
                                                __html: getExampleText()
                                                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                                    .replace(/• /g, '• ')
                                                    .replace(/\n/g, '<br/>')
                                            }} />
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        )}

                        {!showExamples && (
                            <Row className="mt-3">
                                <Col>
                                    <Button 
                                        variant="link" 
                                        size="sm" 
                                        className="text-muted p-0"
                                        onClick={() => setShowExamples(true)}
                                    >
                                        💡 Show example
                                    </Button>
                                </Col>
                            </Row>
                        )}

                        {/* Formatting Toolbar with Week Dropdown */}
                        <Row className="mt-4">
                            <Col>
                                <div className="formatting-toolbar">
                                    <div className="toolbar-group">
                                        <Button 
                                            variant="outline-secondary" 
                                            size="sm"
                                            onClick={() => formatText('bold')}
                                            title="Bold selected text"
                                        >
                                            <strong>B</strong>
                                        </Button>
                                        <Button 
                                            variant="outline-secondary" 
                                            size="sm"
                                            onClick={() => formatText('bullet')}
                                            title="Add bullet point"
                                        >
                                            ☰
                                        </Button>
                                    </div>
                                    <div className="toolbar-group emoji-picker-container">
                                        <Button 
                                            variant="outline-secondary" 
                                            size="sm"
                                            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                            title="Add emoji"
                                        >
                                            😊
                                        </Button>
                                        {showEmojiPicker && (
                                            <div className="emoji-picker-wrapper">
                                                <EmojiPicker
                                                    onEmojiClick={onEmojiClick}
                                                    width={300}
                                                    height={400}
                                                    searchPlaceholder="Search emojis..."
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="toolbar-group">
                                        {showDropdown ? (
                                            <Form.Select 
                                                size="sm" 
                                                value={selectedWeek || ''}
                                                onChange={(e) => handleWeekSelection(parseInt(e.target.value))}
                                                disabled={isLoadingWeek}
                                                className="week-dropdown"
                                            >
                                                {getWeekOptions().map(option => (
                                                    <option key={option.week} value={option.week}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        ) : (
                                        <Form.Select 
                                            size="sm" 
                                            value={currentWeek ? `${currentWeek.season}-${currentWeek.week}` : ''}
                                            disabled
                                            className="week-dropdown"
                                        >
                                            <option value={currentWeek ? `${currentWeek.season}-${currentWeek.week}` : ''}>
                                                {getCurrentWeekDisplay()}
                                            </option>
                                        </Form.Select>
                                        )}
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        {/* Text Input */}
                        <Row className="mt-3">
                            <Col>
                                <Form.Group>
                                    <Form.Control
                                        as="textarea"
                                        id="weeklyContextTextarea"
                                        rows={8}
                                        value={weeklyContext}
                                        onChange={handleTextChange}
                                        disabled={showDropdown ? (selectedWeek !== upcomingWeek) : false}
                                        placeholder={showDropdown && selectedWeek !== upcomingWeek ? 
                                            "This week has already been processed. You can view the context but cannot make changes." :
                                            `Share what's happening in your league this week - trash talk, trades, drama, or anything that will make your recap more personal...

This week the league chat has been absolutely wild. Team Alpha has been talking so much trash about their "guaranteed win" 🙄`}
                                        className="weekly-context-textarea"
                                    />
                                    
                                    {/* Character count - all screen sizes */}
                                    <div className="mt-2 text-end">
                                        <small className={`character-counter ${getRemainingCharacters() < 50 ? 'text-warning' : 'text-muted'}`}>
                                            {getRemainingCharacters()} characters remaining
                                        </small>
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className='align-items-center mt-4 justify-content-center'>
                            {!showDropdown && (
                            <Col xs="auto">
                                <Button 
                                    onClick={handleSkip}
                                    disabled={isSubmitting}
                                    variant="outline-secondary"
                                    className="btn-lg mx-2 onboarding-nav-btn"
                                >
                                    Skip
                                </Button>
                            </Col>
                            )}
                            <Col xs="auto">
                                        <Button 
                                            onClick={handleContinue}
                                            disabled={showDropdown ? (!isSaveEnabled() || isSubmitting) : (isSubmitting || !weeklyContext.trim())}
                                            className={`btn-lg mx-2 onboarding-nav-btn ${showDropdown ? (isSaveEnabled() ? 'btn-primary-gradient' : 'btn-secondary') : (weeklyContext.trim() ? 'btn-primary-gradient' : 'btn-secondary')}`}
                                        >
                                            {isSubmitting ? 'Saving...' : (showDropdown ? 'Save' : 'Next →')}
                                        </Button>
                            </Col>
                        </Row>

                        {/* Warning message for non-editable weeks */}
                        {showDropdown && selectedWeek && selectedWeek !== upcomingWeek && (
                            <Row className="mt-3">
                                <Col>
                                    <div className="alert alert-warning text-center" role="alert">
                                        <i className="bi bi-exclamation-triangle me-2"></i>
                                        This week has already been processed for video creation. You can view the context but cannot make changes.
                                    </div>
                                </Col>
                            </Row>
                        )}
                </>
            ) : (
                // Onboarding mode - keep existing structure
                <div className="pt-5 onboarding-section">    
                    <Row className="onboarding-header">
                        <Col xs={1} md={2}></Col>
                        <Col xs={10} md={8}>       
                            <Row className="text-center onboarding-title-row">
                                <Col className="onboarding-title">Tell Us About This Week</Col>
                            </Row>
                            
                            {/* Progress Bar */}
                            <Row className="mt-4">
                                <Col>
                                    <div className="text-center mb-1">
                                        <span className="onboarding-progress-percentage">
                                            {Math.round(getProgressPercentage())}% Complete
                                        </span>
                                    </div>
                                    <div className="onboarding-progress">
                                        <div 
                                            className="onboarding-progress-bar"
                                            style={{ width: `${getProgressPercentage()}%` }}
                                        ></div>
                                    </div>
                                </Col>
                            </Row>

                            {/* Examples Section */}
                            {showExamples && (
                                <Row className="mt-4">
                                    <Col>
                                        <div className="weekly-context-examples">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <h6 className="examples-title mb-0">💡 Example</h6>
                                                <Button 
                                                    variant="link" 
                                                    size="sm" 
                                                    className="text-muted p-0"
                                                    onClick={() => setShowExamples(false)}
                                                >
                                                    Hide
                                                </Button>
                                            </div>
                                            <div className="examples-content">
                                                <div dangerouslySetInnerHTML={{ 
                                                    __html: getExampleText()
                                                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                                        .replace(/• /g, '• ')
                                                        .replace(/\n/g, '<br/>')
                                                }} />
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            )}

                            {!showExamples && (
                                <Row className="mt-2">
                                    <Col>
                                        <Button 
                                            variant="link" 
                                            size="sm" 
                                            className="text-muted p-0"
                                            onClick={() => setShowExamples(true)}
                                        >
                                            💡 Show example
                                        </Button>
                                    </Col>
                                </Row>
                            )}

                            {/* Formatting Toolbar */}
                            <Row className="mt-4">
                                <Col>
                                    <div className="formatting-toolbar">
                                        <div className="toolbar-group">
                                            <Button 
                                                variant="outline-secondary" 
                                                size="sm"
                                                onClick={() => formatText('bold')}
                                                title="Bold selected text"
                                            >
                                                <strong>B</strong>
                                            </Button>
                                            <Button 
                                                variant="outline-secondary" 
                                                size="sm"
                                                onClick={() => formatText('bullet')}
                                                title="Add bullet point"
                                            >
                                                ☰
                                            </Button>
                                        </div>
                                        <div className="toolbar-group emoji-picker-container">
                                            <Button 
                                                variant="outline-secondary" 
                                                size="sm"
                                                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                                title="Add emoji"
                                            >
                                                😊
                                            </Button>
                                            {showEmojiPicker && (
                                                <div className="emoji-picker-wrapper">
                                                    <EmojiPicker
                                                        onEmojiClick={onEmojiClick}
                                                        width={300}
                                                        height={400}
                                                        searchPlaceholder="Search emojis..."
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        <div className="toolbar-group">
                                            <Form.Select 
                                                size="sm" 
                                                value={currentWeek ? `${currentWeek.season}-${currentWeek.week}` : ''}
                                                disabled
                                                className="week-dropdown"
                                            >
                                                <option value={currentWeek ? `${currentWeek.season}-${currentWeek.week}` : ''}>
                                                    {getCurrentWeekDisplay()}
                                                </option>
                                            </Form.Select>
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            {/* Text Input */}
                            <Row className="mt-3">
                                <Col>
                                    <Form.Group>
                                        <Form.Control
                                            as="textarea"
                                            id="weeklyContextTextarea"
                                            rows={8}
                                            value={weeklyContext}
                                            onChange={handleTextChange}
                                            placeholder={`Share what's happening in your league this week - trash talk, trades, drama, or anything that will make your recap more personal...

This week the league chat has been absolutely wild. Team Alpha has been talking so much trash about their "guaranteed win" 🙄`}
                                            className="weekly-context-textarea"
                                        />
                                        
                                        {/* Character count - all screen sizes */}
                                        <div className="mt-2 text-end">
                                            <small className={`character-counter ${getRemainingCharacters() < 50 ? 'text-warning' : 'text-muted'}`}>
                                                {getRemainingCharacters()} characters remaining
                                            </small>
                                        </div>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row className='align-items-center mt-4 justify-content-center'>
                                <Col xs="auto">
                                    <Button 
                                        onClick={handleSkip}
                                        disabled={isSubmitting}
                                        variant="outline-secondary"
                                        className="btn-lg mx-2 onboarding-nav-btn"
                                    >
                                        Skip
                                    </Button>
                                </Col>
                                <Col xs="auto">
                                    <Button 
                                        onClick={handleContinue}
                                        disabled={isSubmitting || !weeklyContext.trim()}
                                        className={`btn-lg mx-2 onboarding-nav-btn ${weeklyContext.trim() ? 'btn-primary-gradient' : 'btn-secondary'}`}
                                    >
                                        {isSubmitting ? 'Saving...' : 'Next →'}
                                        </Button>
                            </Col>
                        </Row>                                                 
                    </Col>                            
                </Row>
            </div>
            )}
        </>
    );
};

function mapStateToProps({auth}) {
    return { auth };
}

export default connect(mapStateToProps)(WeeklyContextInput);
